import FormLabel from "@mui/material/FormLabel";
import Stack from "@mui/material/Stack";
import { useMemo } from "react";
import AutocompleteField from "@/components/common/formElements/controlled/AutocompleteField";
import CheckboxField from "@/components/common/formElements/controlled/CheckboxField";
import StringToJSX from "@/components/common/StringToJSX";
import { FormConsentDropdownValues } from "@/components/site-builder/modules/CustomFormModule/constants";
import { replaceConsentText } from "@/components/site-builder/modules/CustomFormModule/utils/consent/replaceConsentText";
import { FormBuilderConsentInputType } from "@/constants/form-builder";
import { useTranslation } from "@/i18n/client";
import type { SelectOption } from "@/components/common/formElements/types";

export interface ConsentFieldProps {
  name: string;
  text: string;
  type: FormBuilderConsentInputType;
  required: boolean;
  siteName: string;
}

export default function ConsentField(props: ConsentFieldProps) {
  const { name, text: consentText, type, required, siteName } = props;

  const { t } = useTranslation("form-elements");
  const rules = required ? { required } : undefined;
  const processedText = useMemo(() => {
    return replaceConsentText({
      text: consentText,
      siteName,
    });
  }, [consentText, siteName]);

  switch (type) {
    case FormBuilderConsentInputType.Checkbox: {
      return (
        <CheckboxField
          name={name}
          label={<StringToJSX text={processedText} tag="span" />}
          rules={rules}
        />
      );
    }
    case FormBuilderConsentInputType.Dropdown: {
      const options: SelectOption[] = [
        {
          value: FormConsentDropdownValues.Yes,
          label: t("privacy-consent.field.question.yes.label"),
        },
        {
          value: FormConsentDropdownValues.No,
          label: t("privacy-consent.field.question.no.label"),
        },
      ];

      return (
        <Stack direction="row" spacing={1}>
          <AutocompleteField
            name={name}
            variant="standard"
            disableClearable
            options={options}
            rules={rules}
            fullWidth={false}
            sx={{ minWidth: "100px" }}
            placeholder={t("privacy-consent.field.question.placeholder")}
          />
          <span>
            <StringToJSX text={processedText} tag="span" />
            <FormLabel required={required} />
          </span>
        </Stack>
      );
    }

    default:
      return null;
  }
}
